import { useState, useRef, ChangeEvent, useMemo, MouseEvent, FocusEvent } from 'react'
import { JobApplyForm, JobApplyFormErrors, JobApplyResumeData, JobApplyCoverLetterData } from 'components/core/modal/JobApplyModal/types'
import validator from 'validator'
import { useFileUpload } from 'hooks/use-file-upload/useFileUpload'
import { useHttp } from 'hooks/use-http/useHttp'
import { useNotification } from 'hooks/use-notification/useNotification'
import { useAuth } from 'pages/auth/hooks/useAuth'
import extractOnboardingQuestions from 'common/utils/extractOnboardingQuestions'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { useHistory } from 'react-router-dom'

const useApplyJob = () => {
  const { notify } = useNotification()
  const { backendUser } = useAuth()
  const { getProcessedFile } = useFileUpload()
  const { post, get } = useHttp()
  const { isDevice } = useScreenSizes()
  const { goBack } = useHistory()

  const [isOpen, setIsOpen] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<JobApplyFormErrors>({
    email: { valid: false, touched: false},
    phone: { valid: false, touched: false}
  })
  const [resumeData, setResumeData] = useState({} as JobApplyResumeData)
  const [coverLetterData, setCoverLetterData] = useState({} as JobApplyCoverLetterData)
  const [formData, setFormData] = useState<JobApplyForm>({
    email: backendUser?.username || '',
    phone: '',
    useExistingResume: false,
    countryCode: 'CA +1'
  })

  const inputRef = useRef(null)

  const toggleModal = () => {
    setIsOpen(state => !state)
  }
  const getCountryCode = (code = '') => {
    if (code) {
      let extractCode = code.split(' ')?.[1]
      return extractCode ? extractCode : ''
    }
    return ''
  }

  const handleFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData(state => ({
      ...state,
      [e.target?.name]: e.target?.name === 'useExistingResume' ? e.target?.checked : e.target?.value?.trim()
    }))
  }

  const handleUploadSelect = () => {
    inputRef.current.click()
  }
  const handleInputFileClick = (e: MouseEvent) => {
    const target = e?.target as HTMLInputElement
    target.value = null
  }

 const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
   setErrors(state => ({ ...state, [e?.target?.name]: { ...state[e?.target?.name], touched: true} }))
 }

  useMemo(() => {
    const isEmailValid = validator.isEmail(formData?.email)
      setErrors(state => ({ ...state, email:{...state.email, valid: isEmailValid}}))
  }, [formData?.email])

  useMemo(() => {
    const isPhoneValid = validator.isMobilePhone(`${getCountryCode(formData?.countryCode)}${formData?.phone}`)
    setErrors(state => ({ ...state, phone: { ...state.phone, valid: isPhoneValid } }))

  }, [, formData?.phone, formData?.countryCode])
  
  const handleFormSubmit = (jobId: string, close: () => void, onSuccess) => {
    if (!jobId) return
    setIsLoading(true)
    const data = {
      email: formData?.email,
      phone: `${formData?.countryCode}${formData?.phone}`,
      resume: formData.useExistingResume
        ? extractOnboardingQuestions({ listOfQuestions: backendUser?.onboardingQuestions, q: 'resume' })
        : resumeData?.base64File,
      coverLetter: coverLetterData?.base64File
    }
    post(`job-applicant/${backendUser?.id}/${jobId}`, data)
      .then(() => {
        notify('success', 'Your Application is Submitted Successfully')
        close()
        onSuccess()
        setIsLoading(false)
        if(isDevice){
          goBack()
        }
      })
      .catch(() => {
        setIsLoading(false)
      })
    
  }

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement> | any, fileHandler: 'drag' | 'upload', fileType?: any) => {
    const files = fileHandler === 'drag' ? event.dataTransfer.files : event.target.files
    if (files?.[0]) {
      const fileSize = files[0]?.size
      if (fileSize <= 5000000) {
        getProcessedFile(files, ['docx', 'doc', 'pdf'], 'stream', async ({ file }: { file: File }) => {
          const name = files?.[0]?.name
          const normalizedFileName = files?.[0]?.name.split('.')
          const type = normalizedFileName[normalizedFileName.length - 1]
          const base64File = JSON.stringify({ file, type })

          fileType === 'resume' ? setResumeData({ name, base64File}) : setCoverLetterData({ name, base64File})
        })
      } else {
        notify('error', 'File too large max is 5MB')
      }
    }
  }
  const getJobStatus = async (jobId: string) => {
    if(!backendUser) return;
    return get(`job-applicant/${backendUser?.id}/${jobId}`)
  }
  return {
    handleInputFileClick,
    handleUploadSelect,
    handleFileSelect,
    handleFormSubmit,
    handleFormChange,
    handleOnBlur,
    isLoading,
    errors,
    resumeData,
    formData,
    setResumeData,
    inputRef,
    toggleModal,
    isOpen,
    getJobStatus,
    coverLetterData,
    setCoverLetterData
  } as const
}

export default useApplyJob
